import React, { useState, useEffect, useRef } from "react";
import { metaData } from "@cornerstonejs/core";
import "../../styles/ReportingTool.css";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const ReportingTool = ({viewportId,selectedSeries,onClose,patientData,}) => {
  const reportRef = useRef(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [reportData, setReportData] = useState({
  patientID: "",patientName: "",patientAge: "",patientSex: "",patientBirthDate: "",studyID: "",studyDate: "",studyDescription: "",seriesDescription: "",accessionNumber: "",referringPhysician: "",
  protocol: "",
  observations: "",
  impression: "",
  advice: "",
});
  // =========================================================
  // FORMAT DATE
  // =========================================================
  const formatDate = (dateValue) => {
    if (!dateValue) return "";
    if (
      typeof dateValue === "string" &&
      dateValue.length === 8
    ) {
      const year = dateValue.substring(0, 4);
      const month = dateValue.substring(4, 6);
      const day = dateValue.substring(6, 8);
      return `${day}-${month}-${year}`;
    }
    return "";
  };
  // =========================================================
  // CALCULATE AGE
  // =========================================================
  const calculateAge = (birthDate) => {
    if (!birthDate) return "";
    try {
      let birthDateObj;
      if (
        typeof birthDate === "string" &&
        birthDate.length === 8
      ) {
        const year = parseInt(birthDate.substring(0, 4));
        const month = parseInt(birthDate.substring(4, 6)) - 1;
        const day = parseInt(birthDate.substring(6, 8));
        birthDateObj = new Date(year, month, day);
      } else {
        birthDateObj = new Date(birthDate);
      }
      if (isNaN(birthDateObj.getTime())) return "";
      const today = new Date();
      let years =
        today.getFullYear() -
        birthDateObj.getFullYear();
      const monthDiff =
        today.getMonth() -
        birthDateObj.getMonth();
      if (
        monthDiff < 0 ||
        (
          monthDiff === 0 &&
          today.getDate() <
          birthDateObj.getDate()
        )
      ) {
        years--;
      }
      return `${years} Years`;
    } catch (err) {
      console.error(err);
      return "";
    }
  };
  // =========================================================
  // FETCH DICOM DATA
  // =========================================================
  useEffect(() => {
    const fetchPatientInfo = async () => {
      if (!selectedSeries?.imageIds?.length) return;
      try {
        const imageId =
          `wadouri:${selectedSeries.imageIds[0]}`;
        const patientModule =
          metaData.get("patientModule", imageId) || {};
        const generalStudyModule =
          metaData.get("generalStudyModule", imageId) || {};
          const studyID = generalStudyModule.studyInstanceUID || selectedSeries?.studyId || selectedSeries?.study_id ||"";
        const accessionNumber =generalStudyModule.accessionNumber || "";
const referringPhysician =generalStudyModule.referringPhysicianName || "";
        const generalSeriesModule =metaData.get("generalSeriesModule", imageId) || {};
        let patientName = "";
        if (patientModule.patientName) {
          if (
            typeof patientModule.patientName === "string"
          ) {
            patientName = patientModule.patientName;
          } else if (
            typeof patientModule.patientName === "object"
          ) {
            patientName =
              patientModule.patientName.Alphabetic ||
              patientModule.patientName.FamilyName ||
              "";
          }
        }
        let patientID = "";
        if (patientModule.patientID) {
          patientID =
            typeof patientModule.patientID === "string"
              ? patientModule.patientID
              : String(patientModule.patientID);
        }
        let patientSex = "";
        if (patientModule.patientSex) {
          patientSex =
            typeof patientModule.patientSex === "string"
              ? patientModule.patientSex
              : "";
        }
        let birthDate = "";
        if (patientModule.patientBirthDate) {
          birthDate = formatDate(
            patientModule.patientBirthDate
          );
        }
        let studyDate = "";
        if (generalStudyModule.studyDate) {
          studyDate = formatDate(
            generalStudyModule.studyDate
          );
        }
        const age =
          calculateAge(
            patientModule.patientBirthDate
          );
        setReportData((prev) => ({...prev,patientID,patientName,patientAge: age,patientSex,patientBirthDate: birthDate,studyID,studyDate,accessionNumber,referringPhysician,
  studyDescription:
    generalStudyModule.studyDescription ||
    selectedSeries?.studyDescription ||
    "",
  seriesDescription:
    generalSeriesModule.seriesDescription ||
    selectedSeries?.seriesName ||
    "",
}));
      } catch (err) {
        console.error(
          "Error fetching patient info:",
          err
        );
      }
    };
    fetchPatientInfo();
  }, [selectedSeries]);
  // =========================================================
  // FETCH VOICE REPORT
  // =========================================================
  useEffect(() => {
    const fetchVoiceReport = async () => {
      if (!reportData.patientID) return;
      try {
        setIsLoading(true);
        const response = await fetch(
          `http://localhost:8000/api/get-report/?patient_id=${reportData.patientID}`
        );
        const data = await response.json();
        if (data.success) {
          const voiceData =
            data.report.reportData;
          setReportData((prev) => ({
            ...prev,
            protocol:
              voiceData.technique || "",
            observations: `
${voiceData.clinical_history || ""}
${voiceData.findings || ""}
            `.trim(),
            impression:
              voiceData.impression || "",
            advice:
              voiceData.recommendation || "",
          }));
        }
      } catch (err) {
        console.error(
          "Error fetching report:",
          err
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchVoiceReport();
  }, [reportData.patientID]);
  // =========================================================
  // HANDLE INPUT CHANGE (FOR EDIT MODE)
  // =========================================================
  const handleInputChange = (field, value) => {
    setReportData(prev => ({ ...prev, [field]: value }));
  };
  // =========================================================
  // SAVE REPORT (EDIT MODE)
  // =========================================================
  const handleSave = () => {
    setIsSaved(true);
    setIsEditing(false);
    // Save to localStorage
    const reportToSave = {
      ...reportData,
      timestamp: new Date().toISOString(),
      seriesId: selectedSeries?.seriesId || selectedSeries?.seriesName
    };
    try {
      localStorage.setItem(`report_${selectedSeries?.seriesId || Date.now()}`, JSON.stringify(reportToSave));
    } catch (err) {
      console.error("Error saving to localStorage:", err);
    }
    setTimeout(() => {
      setIsSaved(false);
    }, 2000);
  };
  // =========================================================
  // EDIT MODE TOGGLE
  // =========================================================
  const handleEdit = () => {
    setIsEditing(true);
  };
  // =========================================================
  // PDF DOWNLOAD
  // =========================================================
  const handleDownloadPDF = async () => {
    const element = reportRef.current;
    const canvas =
      await html2canvas(element, {
        scale: 2,
        backgroundColor: '#ffffff'
      });
    const imgData =
      canvas.toDataURL("image/png");
    const pdf =
      new jsPDF("p", "mm", "a4");
    const imgWidth = 210;
    const pageHeight = 295;
    const imgHeight =
      (canvas.height * imgWidth) /
      canvas.width;
    let heightLeft = imgHeight;
    let position = 0;
    pdf.addImage(
      imgData,
      "PNG",
      0,
      position,
      imgWidth,
      imgHeight
    );
    heightLeft -= pageHeight;
    while (heightLeft > 0) {
      position =
        heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(
        imgData,
        "PNG",
        0,
        position,
        imgWidth,
        imgHeight
      );
      heightLeft -= pageHeight;
    }
    pdf.save(
      `MRI_Report_${reportData.patientID || "Patient"}.pdf`
    );
  };
  // =========================================================
  // PRINT
  // =========================================================
  const handlePrint = () => {
    const printContent = reportRef.current.innerHTML;
    const originalTitle = document.title;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>MRI Radiology Report</title>
        </head>
        <body>
          <div class="print-report">
            ${printContent}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
    printWindow.close();
  };
  // =========================================================
  // RENDER SECTION (VIEW MODE)
  // =========================================================
  const renderSection = (title, content) => {
    if (!content) return null;
    return (
      <div className="report-section">
        <div className="section-heading">
          {title}
        </div>
        <div className="section-content">
          {content}
        </div>
      </div>
    );
  };
  // =========================================================
  // RENDER EDITABLE SECTION
  // =========================================================
  const renderEditableSection = (title, field, placeholder = "") => {
    return (
      <div className="report-section">
        <div className="section-heading">{title}</div>
        <textarea
          className="editable-textarea"
          value={reportData[field]}
          onChange={(e) => handleInputChange(field, e.target.value)}
          placeholder={placeholder}
          rows={field === "observations" ? 8 : field === "impression" ? 5 : 4}
        />
      </div>
    );
  };
  // =========================================================
  // UI
  // =========================================================
  return (
    <div className="reporting-tool-overlay">
      <div className="reporting-tool-container">
        {/* HEADER */}
        <div className="report-header">
          <h2>MRI REPORTING TOOL</h2>
          <div className="report-actions">
            {!isEditing ? (
              <>
                <button
                  onClick={handleEdit}
                  className="btn-edit"
                >
                  ✏️ Edit
                </button>
                <button
                  onClick={handlePrint}
                  className="btn-print"
                >
                  🖨 Print
                </button>
                <button
                  onClick={handleDownloadPDF}
                  className="btn-export"
                >
                  📄 PDF
                </button>
                
              </>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  className="btn-save"
                >
                  💾 Save
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  className="btn-cancel"
                >
                  ✖ Cancel
                </button>
              </>
            )}

            <button
              onClick={onClose}
              className="btn-close"
            >
              ×
            </button>
          </div>
        </div>
        {/* SAVE NOTIFICATION */}
        {isSaved && (
          <div className="save-notification">
            ✓ Report saved successfully!
          </div>
        )}
        {/* LOADING */}
        {isLoading && (
          <div className="loading-box">
            Loading Voice Report...
          </div>
        )}
        {/* REPORT PAPER */}
        <div
          className="report-paper"
          ref={reportRef}
        >
          {/* TITLE */}
          <div className="report-main-title">
            MRI RADIOLOGY REPORT
          </div>
          {/* PATIENT INFO (Non-editable) */}
          <div className="patient-info-box">
            <div className="patient-grid">
  <div>
    <strong>Patient Name:</strong>{" "}
    {reportData.patientName || "N/A"}
  </div>
  <div>
    <strong>Patient ID:</strong>{" "}
    {reportData.patientID || "N/A"}
  </div>
  <div>
    <strong>Birth Date:</strong>{" "}
    {reportData.patientBirthDate || "N/A"}
  </div>
  <div>
    <strong>Age:</strong>{" "}
    {reportData.patientAge || "N/A"}
  </div>
  <div>
    <strong>Gender:</strong>{" "}
    {reportData.patientSex || "N/A"}
  </div>
  <div>
    <strong>Study ID:</strong>{" "}
    {reportData.studyID || "N/A"}
  </div>
  <div>
    <strong>Study Date:</strong>{" "}
    {reportData.studyDate || "N/A"}
  </div>
  <div>
    <strong>Study Description:</strong>{" "}
    {reportData.studyDescription || "N/A"}
  </div>
  <div>
    <strong>Series Description:</strong>{" "}
    {reportData.seriesDescription || "N/A"}
  </div>
  <div>
    <strong>Accession No:</strong>{" "}
    {reportData.accessionNumber || "N/A"}
  </div>
  <div>
    <strong>Referring Physician:</strong>{" "}
    {reportData.referringPhysician || "N/A"}
  </div>
  <div>
    <strong>Modality:</strong> MRI
  </div>
</div>
          </div>
          {/* EDITABLE SECTIONS OR VIEW SECTIONS */}
          {isEditing ? (
            <>
              {renderEditableSection(
                "Clinical History / Findings",
                "observations",
                "Enter clinical history and findings..."
              )}
              {renderEditableSection(
                "Technique",
                "protocol",
                "Enter technique / protocol..."
              )}
              {renderEditableSection(
                "Impression / Conclusion",
                "impression",
                "Enter impression / conclusion..."
              )}
              {renderEditableSection(
                "Recommendation",
                "advice",
                "Enter recommendation / advice..."
              )}
            </>
          ) : (
            <>
              {renderSection(
                "Clinical History / Findings",
                reportData.observations
              )}
              {renderSection(
                "Technique",
                reportData.protocol
              )}
              {renderSection(
                "Impression / Conclusion",
                reportData.impression
              )}
              {renderSection(
                "Recommendation",
                reportData.advice
              )}
            </>
          )}
          {/* SIGNATURE */}
          <div className="report-signature">
            <div className="signature-line"></div>
            <div>
              Radiologist Signature
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default ReportingTool;